import { LIST_MARKER_DICT, type ListStyle } from '@/version-types';

/**
 * 根据列表样式和 1-based 序号生成字符型 marker 文本。
 *
 * @param style - CharTypedListStyle，当前字符型列表样式。
 * @param order - number，1-based 列表序号。
 * @returns string，可交给文本引擎测量的 marker 文本。
 */
export function getCharTypedMarkerText(style: Exclude<ListStyle, 'custom'>, order: number): string {
    if (order <= 0) return '';
    if (style === 'decimal') return `${order}.`;

    const dict = LIST_MARKER_DICT[style];

    if (style === 'disc' || style === 'circle' || style === 'square') return dict;

    return `${dict[(order - 1) % dict.length]}.`;
}

/**
 * 把 1-based 自然数转换为 lower-latin 序列。
 *
 * @param n - number，1-based 自然数。
 * @returns string，对应的 lower-latin 序列；非法值返回空字符串。
 */
const toLowerLatinSequence = (n: number): string => {
    if (n <= 0) return '';

    let result = '';
    let remainder = n;

    while (remainder > 0) {
        remainder -= 1;
        result = LIST_MARKER_DICT['lower-latin'][remainder % 26] + result;
        remainder = Math.floor(remainder / 26);
    }

    return result;
};

/**
 * 生成 SVG marker 序号文本。
 *
 * @param orderStyle - SVG marker 的自增样式；`none` 表示不渲染序号。
 * @param order - number，1-based 列表序号。
 * @param options - 选项
 *  - padZero - boolean，可选；decimal 且小于 10 时是否补零。
 * @returns string，SVG marker 内部使用的序号文本，不包含句点。
 */
export function getSVGMarkerNumberText(
    orderStyle: 'none' | 'decimal' | 'lower-latin' | 'upper-roman',
    order: number,
    options?: { padZero?: boolean },
): string {
    const { padZero = false } = options || {};

    if (orderStyle === 'none' || order <= 0) return '';

    if (orderStyle === 'decimal') {
        if (padZero && order < 10) return `0${order}`;
        return String(order);
    }

    if (orderStyle === 'lower-latin') {
        return toLowerLatinSequence(order);
    }

    if (order >= 1 && order <= 10) return LIST_MARKER_DICT['upper-roman'][order - 1];
    if (order === 11) return 'Ⅺ';
    if (order === 12) return 'Ⅻ';

    return LIST_MARKER_DICT['upper-roman'][(order - 1) % 10];
}
